import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Upload, Trash2, Download, Loader2, FileText, Image as ImageIcon } from 'lucide-react';
import api from '../../lib/api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Build the flyer URLs from the preview/prod backend so they resolve in this env.
export const flyerImageUrl = (event, ver = 0) =>
  event?.has_custom_flyer ? `${BACKEND}/api/public/events/${event.slug}/flyer?v=${ver}` : '';
export const flyerPdfUrl = (event) =>
  event?.custom_flyer_pdf_url ? `${BACKEND}/api/public/events/${event.slug}/flyer.pdf` : '';

export const CustomFlyerCard = ({ event, onChange, ver }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const hasFlyer = event.has_custom_flyer;

  const handleFile = async (file) => {
    if (!file) return;
    const okTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'application/pdf'];
    if (!okTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, WEBP, or PDF.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/events/${event.id}/flyer`, fd);
      toast.success('Custom flyer uploaded — now showing on your invite.');
      onChange && onChange(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed. Try a different file.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggleUse = async (v) => {
    setBusy(true);
    try {
      const r = await api.put(`/events/${event.id}`, { use_custom_flyer: v });
      onChange && onChange(r.data);
    } catch (e) {
      toast.error('Could not update');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/events/${event.id}/flyer`);
      toast.success('Custom flyer removed — back to the generated design.');
      onChange && onChange(r.data);
    } catch (e) {
      toast.error('Could not remove');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="custom-flyer-card">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <ImageIcon className="h-5 w-5 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Upload your own flyer</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Made a design in Canva, Photoshop, or got a PDF? Upload it to use instead of the generated flyer. PNG, JPG, or PDF.
          </div>

          {hasFlyer ? (
            <div className="mt-3 space-y-3">
              <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  {event.custom_flyer_type === 'pdf'
                    ? <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    : <ImageIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                  <span className="text-sm truncate">
                    {event.custom_flyer_type === 'pdf' ? 'PDF flyer uploaded' : 'Image flyer uploaded'}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {flyerPdfUrl(event) && (
                    <Button variant="ghost" size="icon" asChild data-testid="download-flyer-pdf">
                      <a href={flyerPdfUrl(event)} target="_blank" rel="noreferrer" title="Download PDF">
                        <Download className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={remove} disabled={busy}
                    title="Remove" data-testid="remove-custom-flyer">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive" />}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/20 px-3 py-2.5">
                <div className="text-sm">
                  <div className="font-medium">Use my uploaded flyer</div>
                  <div className="text-xs text-muted-foreground">When on, guests &amp; emails show your design.</div>
                </div>
                <Switch checked={!!event.use_custom_flyer} onCheckedChange={toggleUse} disabled={busy}
                  data-testid="use-custom-flyer-switch" />
              </div>

              <Button variant="outline" size="sm" className="gap-2" disabled={uploading}
                onClick={() => fileRef.current?.click()} data-testid="replace-flyer-button">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Replace flyer
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="sm" className="gap-2 mt-3" disabled={uploading}
              onClick={() => fileRef.current?.click()} data-testid="upload-flyer-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Upload flyer
            </Button>
          )}

          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="flyer-file-input" />
        </div>
      </div>
    </div>
  );
};
